import React from "react";
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    Bold,
    ChevronLeft,
    ChevronRight,
    Eraser,
    Highlighter,
    Italic,
    List,
    ListOrdered,
    Pin,
    PinOff,
    Printer,
    Redo2,
    Strikethrough,
    Underline as UnderlineIcon,
    Undo2,
} from "lucide-react";
import {
    EditorContent,
    useEditor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import {
    TextStyleKit,
} from "@tiptap/extension-text-style";

const toolbarButtonClass = (isActive) => `
    inline-flex
    h-7
    min-w-7
    items-center
    justify-center
    rounded-md
    border
    px-1
    text-[9px]
    [&>svg]:h-3
    [&>svg]:w-3
    font-semibold
    transition
    ${
        isActive
            ? "border-[#ffd166] bg-[#ffd166]/15 text-[#ffd166]"
            : "border-[#ffd166]/25 bg-[#080b14] text-[#fff3c4] hover:border-[#ffd166]/70 hover:text-[#ffd166]"
    }
`;

const RichNotebookEditor = ({
    content = "",
    onContentChange,
}) => {
    const [
        isWhitePage,
        setIsWhitePage,
    ] = React.useState(() => {
        return (
            localStorage.getItem(
                "liberiCuriosiNotebookWhitePage"
            ) === "true"
        );
    });

    React.useEffect(() => {
        localStorage.setItem(
            "liberiCuriosiNotebookWhitePage",
            String(isWhitePage)
        );
    }, [isWhitePage]);

    const [
        isToolsOpen,
        setIsToolsOpen,
    ] = React.useState(true);

    const [
        areToolsPinned,
        setAreToolsPinned,
    ] = React.useState(true);

    const onContentChangeRef =
        React.useRef(onContentChange);

    React.useEffect(() => {
        onContentChangeRef.current =
            onContentChange;
    }, [onContentChange]);

    const editor = useEditor({
        extensions: [
            StarterKit,
            TextStyleKit,
            Highlight.configure({
                multicolor: true,
            }),
            TextAlign.configure({
                types: [
                    "heading",
                    "paragraph",
                ],
            }),
        ],
        content:
            content || "<p></p>",
        editorProps: {
    attributes: {
        class: [
            "min-h-[360px]",
            "px-5",
            "py-5",
            "text-[#fff3c4]",
            "outline-none",
            "leading-relaxed",
            "[&_p]:mb-3",
            "[&_h1]:mb-4",
            "[&_h1]:text-3xl",
            "[&_h1]:font-bold",
            "[&_h1]:text-[#ffd166]",
            "[&_h2]:mb-3",
            "[&_h2]:text-2xl",
            "[&_h2]:font-bold",
            "[&_h2]:text-[#fff3c4]",
            "[&_h3]:mb-3",
            "[&_h3]:text-xl",
            "[&_h3]:font-semibold",
            "[&_ul]:ml-6",
            "[&_ul]:list-disc",
            "[&_ol]:ml-6",
            "[&_ol]:list-decimal",
            "[&_blockquote]:border-l-4",
            "[&_blockquote]:border-[#ffd166]/45",
            "[&_blockquote]:pl-4",
            "[&_blockquote]:text-[#b0b8d6]",
        ].join(" "),
    },
},

        onUpdate: ({ editor: currentEditor }) => {
            onContentChangeRef.current?.(
                currentEditor.getHTML()
            );
        },
    });

    React.useEffect(() => {
        if (!editor) return;

        const nextContent =
            content || "<p></p>";

        if (
            editor.getHTML() !== nextContent
        ) {
            editor.commands.setContent(
                nextContent,
                {
                    emitUpdate: false,
                }
            );
        }
    }, [content, editor]);

    if (!editor) {
        return (
            <div className="flex min-h-[360px] items-center justify-center rounded-2xl border border-[#ffd166]/20 bg-[#080b14] text-sm text-[#b0b8d6]">
                Opening notebook...
            </div>
        );
    }

    const textStyleAttributes =
        editor.getAttributes("textStyle");

    const currentTextColor =
        textStyleAttributes.color ||
        "#fff3c4";
const printNotebook = () => {
    const printWindow = window.open(
        "",
        "_blank",
        "width=900,height=700"
    );

    if (!printWindow) {
        return;
    }

    printWindow.document.write(`
        <!doctype html>
        <html>
            <head>
                <title>Print Notepad</title>

                <style>
                    body {
                        margin: 40px;
                        color: #111827;
                        background: white;
                        font-family: Georgia, serif;
                        font-size: 16px;
                        line-height: 1.65;
                    }

                    h1,
                    h2,
                    h3 {
                        color: #111827;
                    }

                    img {
                        max-width: 100%;
                    }

                    blockquote {
                        border-left: 4px solid #9ca3af;
                        margin-left: 0;
                        padding-left: 16px;
                    }

                    @media print {
                        body {
                            margin: 0.5in;
                        }
                    }
                </style>
            </head>

            <body>
                ${editor.getHTML()}
            </body>
        </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
};

    return (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[#ffd166]/30 bg-[#080b14]">
 <div
    className={`
        absolute
        left-2
        top-2
        z-30
        max-h-[calc(100%-1rem)]
        overflow-y-auto
        rounded-xl
        border
        border-[#ffd166]/35
        bg-black/95
        shadow-[0_0_20px_rgba(0,0,0,0.40)]
        backdrop-blur-sm
        transition-[width,padding]
        duration-200
        ${
            isToolsOpen
                ? "w-[150px] p-1.5"
                : "w-[40px] p-1"
        }
    `}
>
    <div
    className={`
        mb-1
        flex
        items-center
        gap-1
        ${
            isToolsOpen
                ? "justify-between"
                : "justify-center"
        }
    `}
>
    {isToolsOpen ? (
        <>
            <span className="pl-1 text-[8px] font-semibold uppercase tracking-[0.12em] text-[#d8b4fe]">
                Tools
            </span>

            <button
                type="button"
                onClick={() =>
                    setIsToolsOpen(false)
                }
                title="Collapse tools"
                aria-label="Collapse notebook tools"
                className="
                    inline-flex
                    h-6
                    w-6
                    items-center
                    justify-center
                    rounded-md
                    border
                    border-[#ffd166]/25
                    bg-[#2b1548]/60
                    text-[#fff3c4]
                    hover:border-[#ffd166]
                    hover:text-[#ffd166]
                "
            >
                <ChevronLeft size={11} />
            </button>
        </>
    ) : (
        <button
            type="button"
            onClick={() =>
                setIsToolsOpen(true)
            }
            title="Expand tools"
            aria-label="Expand notebook tools"
            className="
                inline-flex
                h-6
                w-6
                items-center
                justify-center
                rounded-md
                border
                border-[#ffd166]/35
                bg-[#2b1548]/60
                text-[#ffd166]
                hover:border-[#ffd166]
            "
        >
            <ChevronRight size={11} />
        </button>
    )}
</div>
{isToolsOpen && (
    <label
        className="
            mb-1
            flex
            cursor-pointer
            items-center
            gap-1.5
            rounded-md
            border
            border-[#ffd166]/20
            bg-[#080b14]
            px-1.5
            py-1
            text-[9px]
            font-semibold
            text-[#fff3c4]
            hover:border-[#ffd166]/60
        "
        title="Use a white writing page"
    >
        <input
            type="checkbox"
            checked={isWhitePage}
            onChange={(event) =>
                setIsWhitePage(
                    event.target.checked
                )
            }
            className="
                h-3
                w-3
                cursor-pointer
                accent-[#ffd166]
            "
        />

        White Page
    </label>
)}
                <div
    className={
        isToolsOpen
            ? "grid grid-cols-4 gap-1 [&>select]:col-span-4 [&>select]:w-full"
            : "grid grid-cols-1 gap-1 [&>select]:hidden"
    }
>
                    <select
                        aria-label="Text style"
                        value={
                            editor.isActive(
                                "heading",
                                {
                                    level: 1,
                                }
                            )
                                ? "heading1"
                                : editor.isActive(
                                      "heading",
                                      {
                                          level: 2,
                                      }
                                  )
                                ? "heading2"
                                : editor.isActive(
                                      "heading",
                                      {
                                          level: 3,
                                      }
                                  )
                                ? "heading3"
                                : "paragraph"
                        }
                        onChange={(event) => {
                            const value =
                                event.target.value;

                            if (
                                value ===
                                "heading1"
                            ) {
                                editor
                                    .chain()
                                    .focus()
                                    .toggleHeading({
                                        level: 1,
                                    })
                                    .run();

                                return;
                            }

                            if (
                                value ===
                                "heading2"
                            ) {
                                editor
                                    .chain()
                                    .focus()
                                    .toggleHeading({
                                        level: 2,
                                    })
                                    .run();

                                return;
                            }

                            if (
                                value ===
                                "heading3"
                            ) {
                                editor
                                    .chain()
                                    .focus()
                                    .toggleHeading({
                                        level: 3,
                                    })
                                    .run();

                                return;
                            }

                            editor
                                .chain()
                                .focus()
                                .setParagraph()
                                .run();
                        }}
                        className="h-7 rounded-md border border-[#ffd166]/30 bg-[#080b14] px-1.5 text-sm text-[10px] outline-none focus:border-[#ffd166]"                    >
                        <option value="paragraph">
                            Normal
                        </option>

                        <option value="heading1">
                            Title
                        </option>

                        <option value="heading2">
                            Heading
                        </option>

                        <option value="heading3">
                            Subheading
                        </option>
                    </select>

                    <select
                        aria-label="Font family"
                        value={
                            textStyleAttributes.fontFamily ||
                            ""
                        }
                        onChange={(event) => {
                            const value =
                                event.target.value;

                            if (!value) {
                                editor
                                    .chain()
                                    .focus()
                                    .unsetFontFamily()
                                    .run();

                                return;
                            }

                            editor
                                .chain()
                                .focus()
                                .setFontFamily(
                                    value
                                )
                                .run();
                        }}
                        className="h-9 max-w-full rounded-xl border border-[#ffd166]/30 bg-[#080b14] px-3 text-sm text-[#fff3c4] outline-none focus:border-[#ffd166]"
                    >
                        <option value="">
                            Default Font
                        </option>

                        <option value="Arial">
                            Arial
                        </option>

                        <option value="Georgia">
                            Georgia
                        </option>

                        <option value="Verdana">
                            Verdana
                        </option>

                        <option value="'Times New Roman'">
                            Times New Roman
                        </option>

                        <option value="'Courier New'">
                            Courier New
                        </option>

                        <option value="'Trebuchet MS'">
                            Trebuchet
                        </option>
                    </select>

                    <select
                        aria-label="Font size"
                        value={
                            textStyleAttributes.fontSize ||
                            ""
                        }
                        onChange={(event) => {
                            const value =
                                event.target.value;

                            if (!value) {
                                editor
                                    .chain()
                                    .focus()
                                    .unsetFontSize()
                                    .run();

                                return;
                            }

                            editor
                                .chain()
                                .focus()
                                .setFontSize(
                                    value
                                )
                                .run();
                        }}
                        className="h-9 rounded-xl border border-[#ffd166]/30 bg-[#080b14] px-3 text-sm text-[#fff3c4] outline-none focus:border-[#ffd166]"
                    >
                        <option value="">
                            Size
                        </option>

                        <option value="12px">
                            12
                        </option>

                        <option value="14px">
                            14
                        </option>

                        <option value="16px">
                            16
                        </option>

                        <option value="18px">
                            18
                        </option>

                        <option value="20px">
                            20
                        </option>

                        <option value="24px">
                            24
                        </option>

                        <option value="28px">
                            28
                        </option>

                        <option value="32px">
                            32
                        </option>

                        <option value="40px">
                            40
                        </option>
                    </select>

                    <button
                        type="button"
                        title="Bold"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleBold()
                                .run()
                        }
                        className={toolbarButtonClass(
                            editor.isActive(
                                "bold"
                            )
                        )}
                    >
                        <Bold size={17} />
                    </button>

                    <button
                        type="button"
                        title="Italic"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleItalic()
                                .run()
                        }
                        className={toolbarButtonClass(
                            editor.isActive(
                                "italic"
                            )
                        )}
                    >
                        <Italic size={17} />
                    </button>

                    <button
                        type="button"
                        title="Underline"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleUnderline()
                                .run()
                        }
                        className={toolbarButtonClass(
                            editor.isActive(
                                "underline"
                            )
                        )}
                    >
                        <UnderlineIcon
                            size={17}
                        />
                    </button>

                    <button
                        type="button"
                        title="Strikethrough"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleStrike()
                                .run()
                        }
                        className={toolbarButtonClass(
                            editor.isActive(
                                "strike"
                            )
                        )}
                    >
                        <Strikethrough
                            size={17}
                        />
                    </button>

                    <label
                        title="Text color"
                        className="relative inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-xl border border-[#ffd166]/25 bg-[#080b14]"
                    >
                        <span
                            className="h-4 w-4 rounded-full border border-white/40"
                            style={{
                                backgroundColor:
                                    currentTextColor,
                            }}
                        />

                        <input
                            type="color"
                            value={
                                currentTextColor
                            }
                            onChange={(
                                event
                            ) =>
                                editor
                                    .chain()
                                    .focus()
                                    .setColor(
                                        event
                                            .target
                                            .value
                                    )
                                    .run()
                            }
                            className="absolute inset-0 cursor-pointer opacity-0"
                        />
                    </label>
                </div>

<div
    className={
        isToolsOpen
            ? "mt-1 grid grid-cols-4 gap-1"
            : "mt-1 grid grid-cols-1 gap-1"
    }
>
                    <button
                        type="button"
                        title="Bullet list"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleBulletList()
                                .run()
                        }
                        className={toolbarButtonClass(
                            editor.isActive(
                                "bulletList"
                            )
                        )}
                    >
                        <List size={17} />
                    </button>

                    <button
                        type="button"
                        title="Numbered list"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleOrderedList()
                                .run()
                        }
                        className={toolbarButtonClass(
                            editor.isActive(
                                "orderedList"
                            )
                        )}
                    >
                        <ListOrdered
                            size={17}
                        />
                    </button>

                    <button
                        type="button"
                        title="Align left"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .setTextAlign(
                                    "left"
                                )
                                .run()
                        }
                        className={toolbarButtonClass(
                            editor.isActive({
                                textAlign:
                                    "left",
                            })
                        )}
                    >
                        <AlignLeft
                            size={17}
                        />
                    </button>

                    <button
                        type="button"
                        title="Align center"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .setTextAlign(
                                    "center"
                                )
                                .run()
                        }
                        className={toolbarButtonClass(
                            editor.isActive({
                                textAlign:
                                    "center",
                            })
                        )}
                    >
                        <AlignCenter
                            size={17}
                        />
                    </button>

                    <button
                        type="button"
                        title="Align right"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .setTextAlign(
                                    "right"
                                )
                                .run()
                        }
                        className={toolbarButtonClass(
                            editor.isActive({
                                textAlign:
                                    "right",
                            })
                        )}
                    >
                        <AlignRight
                            size={17}
                        />
                    </button>

                    <div className="mx-1 h-7 w-px bg-[#ffd166]/20" />

                    {[
                        "#fff176",
                        "#ffb3c7",
                        "#a7f3d0",
                        "#93c5fd",
                        "#d8b4fe",
                    ].map(
                        (highlightColor) => (
                            <button
                                key={
                                    highlightColor
                                }
                                type="button"
                                title="Highlight selected text"
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleHighlight(
                                            {
                                                color: highlightColor,
                                            }
                                        )
                                        .run()
                                }
                                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#ffd166]/25 bg-[#080b14] transition hover:border-[#ffd166]/70"
                            >
                                <Highlighter
                                    size={17}
                                    style={{
                                        color: highlightColor,
                                    }}
                                />
                            </button>
                        )
                    )}

                    <button
                        type="button"
                        title="Remove highlight"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .unsetHighlight()
                                .run()
                        }
                        className={toolbarButtonClass(
                            false
                        )}
                    >
                        <Highlighter
                            size={17}
                        />
                    </button>

                    <div className="mx-1 h-7 w-px bg-[#ffd166]/20" />

                    <button
                        type="button"
                        title="Undo"
                        disabled={
                            !editor.can().undo()
                        }
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .undo()
                                .run()
                        }
                        className={`${toolbarButtonClass(
                            false
                        )} disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                        <Undo2 size={17} />
                    </button>

                    <button
                        type="button"
                        title="Redo"
                        disabled={
                            !editor.can().redo()
                        }
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .redo()
                                .run()
                        }
                        className={`${toolbarButtonClass(
                            false
                        )} disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                        <Redo2 size={17} />
                    </button>

                    <button
                        type="button"
                        title="Clear formatting"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .unsetAllMarks()
                                .clearNodes()
                                .run()
                        }
                        className={toolbarButtonClass(
                            false
                        )}
                    >
                        <Eraser size={17} />
                    </button>
                    <button
                        type="button"
                        title="Print notebook"
                        aria-label="Print notebook"
                        onClick={printNotebook}
                        className={toolbarButtonClass(false)}
>
    <Printer size={17} />
</button>
                </div>
            </div>

            <div
    className={`
        min-h-0
        flex-1
        overflow-y-auto
        transition-[padding,background-color]
        duration-200
        ${
            isWhitePage
                ? `
                    bg-white
                    [&_.ProseMirror]:!text-[#111827]
                    [&_.ProseMirror]:caret-black
                    [&_.ProseMirror_h1]:!text-[#111827]
                    [&_.ProseMirror_h2]:!text-[#111827]
                    [&_.ProseMirror_h3]:!text-[#111827]
                    [&_.ProseMirror_blockquote]:!text-[#374151]
                `
                : "bg-[#080b14]"
        }
        ${
            isToolsOpen
                ? "pl-[160px]"
                : "pl-[48px]"
        }
    `}
>
                <EditorContent
                    editor={editor}
                />
            </div>
        </div>
    );
};

export default RichNotebookEditor;